import { memo, useCallback } from "react";
import ConnectionIcon from "./ConnectionIcon";
import MCPItemActions from "./MCPItemActions";
import StatusDot from "./StatusDot";
import { useMCPConnectionTest } from "../hooks/useMCPConnectionTest";
import type { MCPConnection } from "../types";
import { useTranslation } from "react-i18next";

type Props = {
  connection: MCPConnection;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
};

const MCPListItem = ({
  connection,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
}: Props) => {
  const { t } = useTranslation();
  const handleSelect = useCallback(
    () => onSelect(connection.id),
    [connection.id, onSelect],
  );

  const handleEdit = useCallback(
    () => onEdit(connection.id),
    [connection.id, onEdit],
  );

  const handleDelete = useCallback(
    () => onDelete(connection.id),
    [connection.id, onDelete],
  );

  // Each row subscribes to its own test query. The shared cache means the detail
  // pane reuses this result instantly when the user opens the row.
  const { status, errorMessage } = useMCPConnectionTest(connection.id);
  const statusTitle =
    status === "offline" ? errorMessage ?? t("mcpPage.status.offline") : undefined;

  return (
    <li
      onClick={handleSelect}
      className={`group flex cursor-pointer items-center justify-between rounded-md border px-4 py-3 transition-colors ${
        isSelected
          ? "border-border bg-primary/10"
          : "border-transparent hover:border-border hover:bg-muted/30"
      }`}
    >
      <div className="flex items-center gap-3">
        <span className="relative">
          <ConnectionIcon connection={connection} size="sm" />
          <StatusDot
            status={status}
            size="sm"
            title={statusTitle}
            ringClassName="absolute -start-1 top-1 ring-2 ring-sidebar"
          />
        </span>

        <div>
          <div className="text-sm font-medium text-foreground">
            {connection.name}
          </div>
          <div className="text-xs text-muted-foreground">
            {connection.createdAt}
          </div>
        </div>
      </div>

      <MCPItemActions
        onViewDetails={handleSelect}
        onEditDetails={handleEdit}
        onRefresh={() => {}}
        onDelete={handleDelete}
      />
    </li>
  );
};

export default memo(MCPListItem);
